import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APIRoutes } from '../global-misc/api-routes';
import { firstValueFrom } from 'rxjs';
import { CreateFormModel, UpdateFormModel, FormModel, FormPageModel } from '../global-data-models/form.model';

export enum OrderDir {
  ASC = 'asc',
  DESC = 'desc',
}

export interface FormPageOptions {
  orgId: string;
  page: number;
  orderDir: OrderDir;
  limit: number;
  search?: string;
  orderBy?: string;
  status?: string;
  isActive?: boolean;
  isTemplate?: boolean;
}

export const DEFAULT_FORM_PAGE_OPTIONS: FormPageOptions = {
  orgId: 'NO_ORG_ID',
  page: 1,
  orderDir: OrderDir.ASC,
  limit: 30,
  orderBy: 'name',
};

@Injectable({ providedIn: 'root' })
export class FormService {
  constructor(private http: HttpClient) {}

  async getForm(orgId: string, formId: string): Promise<FormModel> {
    const apiRoute = APIRoutes.ORG_FORM_BY_ID_ROUTE.replace(':orgId', orgId).replace(':id', formId);
    return await firstValueFrom(this.http.get<FormModel>(apiRoute));
  }

  async getFormByIdentifier(orgId: string, identifier: string, version?: number): Promise<FormModel> {
    let apiRoute: string;
    if (version) {
      apiRoute = APIRoutes.ORG_FORM_VERSION_BY_IDENTIFIER_ROUTE.replace(':orgId', orgId)
        .replace(':identifier', encodeURIComponent(identifier))
        .replace(':version', version.toString());
    } else {
      apiRoute = APIRoutes.ORG_FORM_BY_IDENTIFIER_ROUTE.replace(':orgId', orgId).replace(':identifier', encodeURIComponent(identifier));
    }
    return await firstValueFrom(this.http.get<FormModel>(apiRoute));
  }

  async getAllFormVersions(orgId: string, identifier: string): Promise<FormModel[]> {
    const apiRoute = APIRoutes.ORG_FORM_VERSIONS_BY_IDENTIFIER_ROUTE.replace(':orgId', orgId).replace(':identifier', encodeURIComponent(identifier));
    return await firstValueFrom(this.http.get<FormModel[]>(apiRoute));
  }

  async getFormsPage(options: FormPageOptions): Promise<FormPageModel> {
    const apiRoute = new URL(APIRoutes.ORG_FORMS_ROUTE.replace(':orgId', options.orgId));

    // Add query params
    apiRoute.searchParams.append('page', String(options.page));
    apiRoute.searchParams.append('orderDir', String(options.orderDir));
    apiRoute.searchParams.append('limit', String(options.limit));
    
    if (options.search) apiRoute.searchParams.append('search', String(options.search));
    if (options.orderBy) apiRoute.searchParams.append('orderBy', String(options.orderBy));
    if (options.status) apiRoute.searchParams.append('status', String(options.status));
    if (typeof options.isActive === 'boolean') apiRoute.searchParams.append('isActive', String(options.isActive));
    if (typeof options.isTemplate === 'boolean') apiRoute.searchParams.append('isTemplate', String(options.isTemplate));

    return await firstValueFrom(this.http.get<FormPageModel>(apiRoute.toString()));
  }

  async createForm(orgId: string, form: CreateFormModel): Promise<FormModel> {
    const apiRoute = APIRoutes.ORG_FORMS_ROUTE.replace(':orgId', orgId);
    return await firstValueFrom(this.http.post<FormModel>(apiRoute, form));
  }

  async updateForm(orgId: string, formId: string, form: UpdateFormModel): Promise<FormModel> {
    const apiRoute = APIRoutes.ORG_FORM_BY_ID_ROUTE.replace(':orgId', orgId).replace(':id', formId);
    return await firstValueFrom(this.http.patch<FormModel>(apiRoute, form));
  }

  async createNewVersion(orgId: string, identifier: string, form: CreateFormModel): Promise<FormModel> {
    const apiRoute = APIRoutes.ORG_FORM_VERSIONS_BY_IDENTIFIER_ROUTE.replace(':orgId', orgId).replace(':identifier', encodeURIComponent(identifier));
    return await firstValueFrom(this.http.post<FormModel>(apiRoute, form));
  }

  async deleteForm(orgId: string, formId: string): Promise<void> {
    const apiRoute = APIRoutes.ORG_FORM_BY_ID_ROUTE.replace(':orgId', orgId).replace(':id', formId);
    return await firstValueFrom(this.http.delete<void>(apiRoute));
  }

  async deleteAllForms(orgId: string): Promise<void> {
    const apiRoute = APIRoutes.ORG_FORMS_ROUTE.replace(':orgId', orgId);
    return await firstValueFrom(this.http.delete<void>(apiRoute));
  }
}
